import React, {
  useMemo, useState, useCallback, useEffect,
} from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { conditionsSelector, diagnosesSelector } from '@redux/dictionaries';
import { getConditions, getDiagnoses } from '@redux/dictionaries/thunks';
import { updateProfile } from '@redux/auth/thunks';
import { userSelector, actionResultSelector } from '@redux/auth';
import { useMount } from '@hooks';
import { handleLoading, handleOperationSuccess } from '@utils';
import { Form, Radio, Select } from 'antd';
import Icon from '@components/Icon';
import Button from '@components/Button';
import './style.scss';

const UpdateInfoForm = () => {
  const dispatch = useDispatch();
  const [isFormFilled, setIsFormFilled] = useState(false);
  const [form] = Form.useForm();

  const user = useSelector(userSelector);
  const diagnoses = useSelector(diagnosesSelector);
  const conditions = useSelector(conditionsSelector);
  const isAffected = user?.profile?.haveBeenAffectedByCancer;
  const actionResult = useSelector(actionResultSelector);

  const onFieldsChange = (changedField, allFields) => {
    setIsFormFilled(true);
  };

  useMount(() => {
    dispatch(getDiagnoses());
    dispatch(getConditions());
  });

  const onFinish = useCallback((values) => {
    const {
      countryId,
      ageGroupId,
      diagnosisId,
      currentConditionId,
    } = user.profile;

    const userProfileValues = {
      countryId,
      ageGroupId,
      diagnosisId,
      currentConditionId,
      haveBeenAffectedByCancer: !isAffected,
    };

    dispatch(updateProfile({
      ...userProfileValues,
      diagnosisId: values.diagnosis,
      currentConditionId: values.condition,
    }));
  }, [user?.profile, isAffected, dispatch]);

  const formItems = useMemo(() => [
    {
      name: 'diagnosis',
      label: <div className='form-field__label field-text-md tablet-field-text-md mobile-field-text-md'>What is your cancer diagnosis?</div>,
      child: () => <Select
        className='form-field__element'
        options={diagnoses.map(({ id, name }) => ({ value: id, label: name }))}
        suffixIcon={<Icon type='chevron-down' />}
        placeholder='Please select diagnosis'
        popupClassName='modal-dropdown'
      />,
    },
    {
      name: 'condition',
      label: <div className='form-field__label field-text-md tablet-field-text-md mobile-field-text-md'>What’s your current condition?</div>,
      child: () => <Radio.Group className='filter-btns field-text tablet-field-text mobile-label-sm'>
        {conditions.map(({
          id,
          name,
        }) => (
          <Radio key={id} className='filter-btn filter-btn_main' value={id}>{name}</Radio>
        ))}
      </Radio.Group>,
    },
  ], [diagnoses, conditions]);

  useEffect(() => {
    if (handleOperationSuccess(actionResult, 'updateProfile')) {
      form.resetFields();
    }
  }, [actionResult.status, form]);

  useEffect(() => {
    if (user?.profile?.diagnosisId && user?.profile?.currentConditionId) {
      setIsFormFilled(true);
    }
  }, [user?.profile]);

  return (
    <Form
      layout='vertical'
      className='update-info-form'
      action='#'
      initialValues={{
        diagnosis: user?.profile?.diagnosisId,
        condition: user?.profile?.currentConditionId,
      }}
      form={form}
      onFieldsChange={onFieldsChange}
      onFinish={onFinish}
    >
      <div className='form-fields'>
        {
          formItems.map(({ child, ...itemProps }) => (
            <div className='form-fields__item' key={itemProps.name}>
              <Form.Item
                {...itemProps}
                className='form-field form-field_lg form-field_border'
              >
                {child()}
              </Form.Item>
            </div>
          ))
        }
      </div>
      <div className='form-submit'>
        <Button
          className='field-text tablet-field-text mobile-field-text'
          type='submit'
          theme='main'
          title='Save and continue'
          size='md'
          disabled={!isFormFilled}
          loading={handleLoading(actionResult, 'updateProfile')}
        />
      </div>
    </Form>
  );
};

export default UpdateInfoForm;
